import { DeleteOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { Button, Popconfirm, Upload, Modal, Select, Alert, Spin } from "antd";
import {
  ModalForm,
  ProForm,
  ProFormText,
  ProFormSelect,
  ProFormCheckbox,
} from "@ant-design/pro-components";
import FormItem from "antd/es/form/FormItem";
import { useEffect, useState } from "react";
import SipuniExtensionField from "./SipuniExtensionField";
import { 
  fetchRoles, 
  updateUser, 
  fetchUserLeadsAndClients, 
  fetchSalesManagers, 
  reassignLeadsAndClients 
} from "../utils/fetch";
import axios from "axios";
import config from "../../../config.json";

const UpdateUser = ({ data, companyPermissions, fetch }) => {
  const [roles, setRoles] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sipuniExtension, setSipuniExtension] = useState(data?.sipuniExtension || "");
  const [sipuniEnabled, setSipuniEnabled] = useState(
    typeof data?.sipuniEnabled === "boolean" ? data.sipuniEnabled : true
  );
  
  // State для модального окна переназначения
  const [isReassignModalVisible, setIsReassignModalVisible] = useState(false);
  const [salesManagers, setSalesManagers] = useState([]);
  const [selectedTargetManager, setSelectedTargetManager] = useState(null);
  const [leadsCount, setLeadsCount] = useState(0);
  const [clientsCount, setClientsCount] = useState(0);
  const [isLoadingManagers, setIsLoadingManagers] = useState(false);
  const [isReassigning, setIsReassigning] = useState(false);
  
  const avatarState = () => {
    if (!data?.imgPath) {
      return [];
    }
    const imgSrc = `${config.service}/api/file/photos/avatars/${data.imgPath}`;
    return [{
      uid: '-1',
      name: 'image.png',
      status: 'done',
      url: imgSrc,
    }]
  }
  const [fileList, setFileList] = useState(avatarState());

  useEffect(() => {
    fetchRoles(setRoles);
  }, [data._id]);

  useEffect(() => {
    if (isVisible) {
      setSipuniExtension(data?.sipuniExtension || "");
      setSipuniEnabled(
        typeof data?.sipuniEnabled === "boolean" ? data.sipuniEnabled : true
      );
    }
  }, [data, isVisible]);

  useEffect(() => {
    if (!Array.isArray(roles)) {
      return;
    }

    const newList = [];
    for (let role of roles) {
      newList.push({
        value: role._id,
        label: role.name,
      });
    }
    setRoleOptions(newList);
  }, [roles]);

  // Проверить, относится ли пользователь к отделам продаж
  const isSalesDepartmentUser = () => {
    const companyName = data?.companyId?.name || "";
    return companyName === "Отдел продаж" || companyName === "Отдел продаж Астана";
  };

  // Обработчик нажатия на кнопку удаления
  const handleDeleteClick = async () => {
    const isSalesDept = isSalesDepartmentUser();
    
    // Для менеджеров из отделов продаж - всегда показываем модальное окно переназначения
    if (isSalesDept) {
      setIsReassignModalVisible(true);
      setIsLoadingManagers(true);
      
      // Загружаем количество лидов (в фоновом режиме)
      fetchUserLeadsAndClients(data._id)
        .then(counts => {
          setLeadsCount(counts.leadsCount || 0);
          setClientsCount(counts.clientsCount || 0);
        })
        .catch(() => {
          setLeadsCount(0);
          setClientsCount(0);
        });
      
      // Загружаем список менеджеров
      fetchSalesManagers(data._id)
        .then(managers => {
          if (managers && Array.isArray(managers) && managers.length > 0) {
            setSalesManagers(managers);
          } else {
            setSalesManagers([]);
          }
          setIsLoadingManagers(false);
        })
        .catch(() => {
          setSalesManagers([]);
          setIsLoadingManagers(false);
        });
      
      return;
    }
    
    // Для остальных пользователей - простое подтверждение
    Modal.confirm({
      title: "Удалить пользователя?",
      content: `Вы уверены, что хотите удалить пользователя ${data.name} ${data.surname}?`,
      okText: "Да",
      cancelText: "Нет",
      okButtonProps: { danger: true },
      onOk: async () => {
        setIsLoading(true);
        await deleteUser();
      }
    });
  };

  const deleteUser = () => {
    return axios
      .delete(`${config.service}/api/user/${data._id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      })
      .then(() => {
        fetch();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Переназначить и удалить
  const handleReassignAndDelete = async () => {
    if (!selectedTargetManager) {
      return;
    }
    
    setIsReassigning(true);
    
    try {
      // Проверяем, это демо-режим или реальные данные
      const isDemoMode = selectedTargetManager.startsWith('demo');
      
      if (isDemoMode) {
        // Демо-режим: симулируем успешное переназначение
        setTimeout(() => {
          Modal.success({
            title: "Демо-режим",
            content: "Это демонстрация функционала. В реальном режиме здесь произойдет переназначение лидов и удаление пользователя.",
          });
          setIsReassignModalVisible(false);
          setSelectedTargetManager(null);
          setIsReassigning(false);
        }, 1000);
      } else {
        // Реальный режим: переназначаем лиды и клиенты
        const success = await reassignLeadsAndClients(data._id, selectedTargetManager);
        
        if (success) {
          // Удаляем пользователя
          await deleteUser();
          setIsReassignModalVisible(false);
          setSelectedTargetManager(null);
        }
        setIsReassigning(false);
      }
    } catch {
      setIsReassigning(false);
    }
  };

  const onChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const onPreview = async (file) => {
    let src = file.url;

    if (!src) {
      src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);

        reader.onload = () => resolve(reader.result);
      });
    }

    const image = new Image();
    image.src = src;
    const imgWindow = window.open(src);
    imgWindow?.document.write(image.outerHTML);
  };

  const getBody = (values) => {
    const formdata = new FormData();

    formdata.append('name', values.name);
    formdata.append('surname', values.surname);
    formdata.append('phone', values.phone);
    formdata.append('password', values.password || '');
    formdata.append('roleId', values.roleId);
    formdata.append('companyId', data.companyId._id);
    formdata.append('createLead', values.createLead);
    formdata.append('clients', values.clients);
    formdata.append('specification', values.specification);
    formdata.append('act', values.act);
    formdata.append('agreement', values.agreement);
    formdata.append('KP', values.KP);
    formdata.append('payment', values.payment);
    formdata.append('deliveryCertificate', values.deliveryCertificate);
    formdata.append('acceptanceAct', values.acceptanceAct);
    formdata.append('products', values.products);
    formdata.append('production', values.production);
    formdata.append('orderStatus', values.orderStatus);
    formdata.append('assemblyStatus', values.assemblyStatus);
    formdata.append('manufactureStatus', values.manufactureStatus);
    formdata.append('deliveryStatus', values.deliveryStatus);
    formdata.append('installationStatus', values.installationStatus);
    formdata.append('distribution', values.distribution);
    formdata.append('viewAllFinances', Boolean(values.viewAllFinances));
    formdata.append('changeState', values.changeState);
    formdata.append('changeStatus', values.changeStatus);
    // Kepler Sales Hub permissions
    formdata.append('keplerRob', Boolean(values.keplerRob));
    formdata.append('keplerRobAdmin', Boolean(values.keplerRobAdmin));

    const enabledValue =
      typeof values.sipuniEnabled === "boolean" ? values.sipuniEnabled : sipuniEnabled;
    formdata.append("sipuniEnabled", enabledValue);
    if (sipuniExtension) {
      formdata.append("sipuniExtension", sipuniExtension);
      formdata.append("sipuniOperatorId", sipuniExtension);
    } else {
      formdata.append("sipuniExtension", "");
      formdata.append("sipuniOperatorId", "");
    }

    // Обработка фото
    if (fileList.length > 0 && fileList[0].originFileObj) {
      // Новое фото загружено
      const image = fileList[0].originFileObj;
      formdata.append("image", image, image.name);
    } else if (fileList.length === 0) {
      // Фото удалено
      formdata.append("removeImage", "true");
    }
    // Если fileList содержит только старое фото (url), ничего не добавляем
    return formdata;
  };

  const handleUpdate = (values) => {
    const formdata = getBody(values);
    updateUser(data._id, formdata,() => {setIsVisible(false);});
  }

  return (
    <>
      {/* Модальное окно для переназначения лидов */}
      <Modal
        title="Переназначение лидов и клиентов"
        visible={isReassignModalVisible}
        open={isReassignModalVisible}
        onCancel={() => {
          if (!isReassigning) {
            setIsReassignModalVisible(false);
            setSelectedTargetManager(null);
          }
        }}
        onOk={handleReassignAndDelete}
        okText="Переназначить и удалить"
        cancelText="Отмена"
        okButtonProps={{ 
          disabled: !selectedTargetManager || isReassigning,
          loading: isReassigning,
          danger: true
        }}
        cancelButtonProps={{ disabled: isReassigning }}
        closable={!isReassigning}
        maskClosable={false}
        destroyOnClose={false}
        zIndex={9999}
        style={{ zIndex: 9999 }}
        wrapClassName="user-reassign-modal"
      >
        {isLoadingManagers ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
            <div style={{ marginTop: 10 }}>Загрузка менеджеров...</div>
          </div>
        ) : (
          <>
            <Alert
              message="Переназначение лидов и клиентов"
              description={
                <div>
                  <p>
                    Перед удалением пользователя <strong>{data.name} {data.surname}</strong> необходимо выбрать менеджера, 
                    на которого будут переназначены его лиды и клиенты.
                  </p>
                  {(leadsCount > 0 || clientsCount > 0) && (
                    <div style={{ marginTop: 10 }}>
                      <p><strong>Текущее количество:</strong></p>
                      <ul style={{ marginBottom: 0 }}>
                        {leadsCount > 0 && <li>Лидов: {leadsCount}</li>}
                        {clientsCount > 0 && <li>Клиентов: {clientsCount}</li>}
                      </ul>
                    </div>
                  )}
                  {(leadsCount === 0 && clientsCount === 0) && (
                    <p style={{ marginTop: 10, fontStyle: 'italic', color: '#666' }}>
                      У пользователя нет активных лидов и клиентов, но для безопасности процесса удаления необходимо выбрать менеджера.
                    </p>
                  )}
                  <p style={{ marginTop: 10 }}>
                    Выберите менеджера из отделов продаж, на которого нужно переназначить все данные перед удалением.
                  </p>
                </div>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            {salesManagers.length === 0 ? (
              <Alert
                message="Нет доступных менеджеров"
                description="В отделах продаж нет других активных менеджеров. Удаление невозможно."
                type="error"
                showIcon
              />
            ) : (
              <div>
                <div style={{ marginBottom: 8 }}>
                  <strong>Выберите менеджера:</strong>
                </div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Выберите менеджера"
                  value={selectedTargetManager}
                  onChange={setSelectedTargetManager}
                  disabled={isReassigning}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  dropdownStyle={{ zIndex: 10000 }}
                >
                  {salesManagers.map((manager) => (
                    <Select.Option key={manager._id} value={manager._id}>
                      {manager.name} {manager.surname} 
                      {manager.companyId?.name ? ` (${manager.companyId.name})` : ''}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}
          </>
        )}
      </Modal>
      
      <div className="d-flex align-items-center justify-content-between">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 5,
          }}
          className="me-2"
        >
        <Button
          loading={isLoading}
          onClick={handleDeleteClick}
          className="text-danger px-1"
          style={{ width: 35 }}
          icon={<DeleteOutlined />}
        ></Button>
        </div>
      <ModalForm
        visible={isVisible}
        onVisibleChange={setIsVisible}
        title={data.name + " " + data.surname}
        trigger={
          <div
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button icon={<MenuUnfoldOutlined />}></Button>
          </div>
        }
        initialValues={{
          ...data,
          ...data.permission,
          roleId: data.roleId?._id || "",
          companyId: data.companyId?._id || "",
          phone: data.phone,
          password: "",
          sipuniEnabled:
            typeof data?.sipuniEnabled === "boolean" ? data.sipuniEnabled : true,
        }}
        onFinish={(values) => {
          handleUpdate(values);
          // updateUser(
          //   data._id,
          //   {
          //     ...values,
          //     companyId: data.companyId,
          //   },
          //   () => {
          //     setIsVisible(false);
          //   }
          // );
        }}
      >
        <ProForm.Group>
          <div>
            <ProForm.Group>
              <ProFormText name="name" label="Имя" />
              <ProFormText name="surname" label="Фамилия" />
            </ProForm.Group>
            <ProForm.Group>
              <ProFormText name="phone" label="Номер телефона" />
              <ProFormText.Password
                name="password"
                label="Пароль"
                placeholder="Введите новый пароль"
              />
            </ProForm.Group>
          </div>
          <ProFormSelect name="roleId" label="Роль" options={roleOptions} />
          <FormItem label="Фото">
              <Upload
                listType="picture-card"
                fileList={fileList}
                onChange={onChange}
                onPreview={onPreview}
                accept={".png, .jpeg, .jpg"}
                max={1}
              >
                {fileList.length < 1 && "+ Загрузить"}
              </Upload>
            </FormItem>
        </ProForm.Group>
        <ProForm.Group>
          <div style={{ width: "100%" }}>
            <SipuniExtensionField
              value={sipuniExtension}
              onChange={setSipuniExtension}
              disabled={isLoading}
              required={false}
            />
          </div>
        </ProForm.Group>
        <ProForm.Group>
          <ProFormCheckbox
            name="sipuniEnabled"
            fieldProps={{
              checked: sipuniEnabled,
              onChange: (e) => setSipuniEnabled(e.target.checked),
            }}
          >
            Включить интеграцию с телефонией Sipuni
          </ProFormCheckbox>
        </ProForm.Group>
        <ProForm.Group>
          <ProFormCheckbox
            disabled={!companyPermissions?.createLead}
            name="createLead"
          >
            Создание заявки
          </ProFormCheckbox>
          <ProFormCheckbox
            disabled={!companyPermissions?.clients}
            name="clients"
          >
            Доступ к клиентам
          </ProFormCheckbox>
          <ProFormCheckbox
            disabled={!companyPermissions?.keplerRob}
            name="keplerRob"
          >
            Kepler Sales Hub (доступ)
          </ProFormCheckbox>
          <ProFormCheckbox
            disabled={!companyPermissions?.keplerRobAdmin}
            name="keplerRobAdmin"
          >
            Kepler Sales Hub (админ)
          </ProFormCheckbox>
        </ProForm.Group>
        <ProForm.Group>
          <ProFormCheckbox
            disabled={!companyPermissions?.specification}
            name="specification"
          >
            Спецификация
          </ProFormCheckbox>
          <ProFormCheckbox disabled={!companyPermissions?.act} name="act">
            Заявка на производство
          </ProFormCheckbox>
          <ProFormCheckbox
            disabled={!companyPermissions?.agreement}
            name="agreement"
          >
            Договор
          </ProFormCheckbox>
          <ProFormCheckbox disabled={!companyPermissions?.KP} name="KP">
            КП
          </ProFormCheckbox>
        </ProForm.Group>
        <ProForm.Group>
          <ProFormCheckbox
            disabled={!companyPermissions?.payment}
            name="payment"
          >
            Счет на оплату
          </ProFormCheckbox>
          <ProFormCheckbox
              disabled={!companyPermissions?.deliveryCertificate}
              name="deliveryCertificate"
            >
              Акт приема-передачи
            </ProFormCheckbox>
            <ProFormCheckbox
              disabled={!companyPermissions?.acceptanceAct}
              name="acceptanceAct"
            >
              Акт выполненных работ
            </ProFormCheckbox>
        </ProForm.Group>
        <ProForm.Group>
          <ProFormCheckbox
            disabled={!companyPermissions?.products}
            name="products"
          >
            Товары
          </ProFormCheckbox>
          <ProFormCheckbox
            disabled={!companyPermissions?.orderStatus}
            name="orderStatus"
          >
            Статусы заказов
          </ProFormCheckbox>
          <ProFormCheckbox
            disabled={!companyPermissions?.production}
            name="production"
          >
            Запуск на производство
          </ProFormCheckbox>
        </ProForm.Group>
        <ProForm.Group>
          <ProFormCheckbox
            disabled={!companyPermissions?.changeState}
            name="changeState"
          >
            Принятие / отклонение заявок
          </ProFormCheckbox>
          <ProFormCheckbox
            disabled={!companyPermissions?.changeStatus}
            name="changeStatus"
          >
            Изменение статуса
          </ProFormCheckbox>
        </ProForm.Group>
        <ProForm.Group>
            <ProFormCheckbox name="assemblyStatus" disabled={!companyPermissions?.assemblyStatus}>
              Статусы сборки
            </ProFormCheckbox>
            <ProFormCheckbox name="manufactureStatus" disabled={!companyPermissions?.manufactureStatus}>
              Статусы производства
            </ProFormCheckbox>
            <ProFormCheckbox name="deliveryStatus" disabled={!companyPermissions?.deliveryStatus}>
              Статусы доставки
            </ProFormCheckbox>
            <ProFormCheckbox name="installationStatus" disabled={!companyPermissions?.installationStatus}>
              Статусы монтажа
            </ProFormCheckbox>
            <ProFormCheckbox name="distribution" disabled={!companyPermissions?.distribution}>
              Распределение
            </ProFormCheckbox>
        </ProForm.Group>
        <ProForm.Group>
          <ProFormCheckbox
            name="viewAllFinances"
            disabled={companyPermissions?.viewAllFinances === false}
          >
            Просмотр всех финансов
          </ProFormCheckbox>
        </ProForm.Group>
      </ModalForm>
      </div>
    </>
  );
};

export default UpdateUser;

